// The desktop's bell for an RF sensor event.
//
// ⚠️ THE PHONE'S BELL IS A PUSH NOTIFICATION, and a desktop browser gets none:
// FCM is native-only here (docs/16). So the desktop rings for itself while a
// location page is open — it hears the same `RfV(...)` the tile already moves on
// (useRfSensors) and plays a tone. Nothing here publishes or acts.
//
// ⚠️ THE SAME SOUND AS THE PHONE AND AS THE ▶ TEST (owner, 2026-10-08): the
// file the server would push — the code's own NotifySound, else the sensor's,
// else its type's (a door's Close = main_door_closed), exactly
// rfDevices.js messageFor + planAlert. Played from /sounds (soundPreview.js).
// Where /sounds is not served the file cannot play, and a synthesised chime
// rings instead — a bell that is the wrong tone beats no bell.
//
// ⚠️ A BROWSER PLAYS NOTHING UNTIL THE PAGE HAS HAD A CLICK OR KEY PRESS — and
// it says nothing either: play() just rejects. Every reload resets it (a dev
// server reloads on every edit), so "Door Sensor 1 is unmuted and silent" was
// this, with every other link working (2026-10-10). ringRfBell therefore
// resolves to 'blocked' and the page shows "click to enable sound".

import { rfType, isCloseEvent } from '../../core/devices/rfTypes';
import { soundUrl } from '../../core/push/soundPreview';

/**
 * Does this event ring? The code's own Notify wins (1 on / 0 off); -1 or
 * absent follows the sensor's. The same rule the server applies before it
 * pushes — per-person mutes and the alert grid are server-side and not seen here.
 */
export function rfEventRings(sensor, code) {
  // ⚠️ WHO is told reaches the desktop too (2026-10-12): the server computes,
  // for the viewer the sensor list was loaded for, whether THEIR desktop rings —
  // 0 when people are ticked in "Who is told" and this viewer is not one with
  // Desktop on (or muted it). Absent (older server) = ring as before.
  if (sensor && sensor.ViewerDesktop != null && Number(sensor.ViewerDesktop) === 0) return false;
  const c = (sensor?.Codes || []).find((x) => String(x.REMOTEVALUE) === String(code));
  const own = c && c.Notify != null ? Number(c.Notify) : -1;
  if (own === 0 || own === 1) return own === 1;
  return Number(sensor?.Notify) === 1;
}

/** The sound the server would push for this event (see the note at the top). */
export function rfEventSound(sensor, meaning, code) {
  const c = (sensor?.Codes || []).find((x) => String(x.REMOTEVALUE) === String(code));
  const own = String(c?.NotifySound || '').trim() || String(sensor?.NotifySound || '').trim();
  if (own) return own;
  const t = rfType(sensor?.RfType);
  if (t.key === 'door' && isCloseEvent(meaning)) return 'main_door_closed';
  return t.sound;
}

let ctx = null;
const audio = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!ctx) ctx = new AC();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    return ctx;
  } catch (e) {
    return null;
  }
};

function tone(ac, freq, at, dur, gain) {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = 'sine';
  o.frequency.setValueAtTime(freq, at);
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(gain, at + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  o.connect(g).connect(ac.destination);
  o.start(at);
  o.stop(at + dur + 0.05);
}

// One at a time: a new event cuts the previous sound off, as on the ▶ test.
let playing = null;

const isBlocked = (e) => e && (e.name === 'NotAllowedError' || /user (didn.t|did not) interact|gesture/i.test(String(e.message)));

/**
 * Ring for one event — the real sound file, or the chime if it cannot play.
 * -> Promise<'played' | 'chime' | 'blocked' | 'silent'>; 'blocked' = the browser refused
 *    sound until the page is clicked (see the note at the top).
 */
export async function ringRfBell(sensor, meaning, code) {
  const sound = rfEventSound(sensor, meaning, code);
  // Silent on the phone = silent here: the on-screen alert only.
  if (sound === 'silent') return 'silent';
  const url = soundUrl(sound);
  if (!url) return chime(sensor, meaning) ? 'chime' : 'blocked';
  try {
    if (playing) playing.pause();
    const a = new Audio(url);
    playing = a;
    a.onended = () => { if (playing === a) playing = null; };
    await a.play();
    return 'played';
  } catch (e) {
    if (isBlocked(e)) return 'blocked';
    return chime(sensor, meaning) ? 'chime' : 'blocked';
  }
}

/**
 * The fallback: a two-tone chime; an alarm-level sensor (safe, fire, gas,
 * panic…) rings three times, louder; a close is one soft note.
 */
function chime(sensor, meaning) {
  const ac = audio();
  if (!ac || ac.state === 'suspended') return false;   // suspended = blocked too
  const now = ac.currentTime + 0.01;
  if (isCloseEvent(meaning)) { tone(ac, 660, now, 0.35, 0.15); return true; }
  const alarm = rfType(sensor?.RfType).level === 'alarm';
  const rounds = alarm ? 3 : 1;
  for (let i = 0; i < rounds; i += 1) {
    const t = now + i * 0.7;
    tone(ac, 880, t, 0.45, alarm ? 0.5 : 0.3);
    tone(ac, 660, t + 0.25, 0.6, alarm ? 0.5 : 0.3);
  }
  return true;
}
